export const MIC_AUDIO_SAMPLE_RATE = 48000;
export const MIC_POST_RNNOISE_GAIN = 2.0;

export const MIC_AUDIO_PROCESSING_LABELS = Object.freeze({
  RNNOISE_WITH_GATE: "browser-aec+rnnoise+light-gate",
  NATIVE: "browser-aec+native-ns-agc",
  NATIVE_WITH_USER_SETTINGS: "native-user-settings"
});

const LIGHT_GATE_OPTIONS = Object.freeze({
  openThreshold: -50,
  closeThreshold: -60,
  holdMs: 120,
  maxChannels: 1
});

const MIC_LIMITER_OPTIONS = Object.freeze({
  threshold: -6,
  knee: 0,
  ratio: 20,
  attack: 0.003,
  release: 0.08
});

const noiseSuppressorModulePromiseCache = new WeakMap();
const rnnoiseWasmBinaryPromiseCache = new WeakMap();

function normalizeMicProcessingPreferences(micProcessingPreferences = {}) {
  return {
    disableEchoCancellation: micProcessingPreferences.disableEchoCancellation === true,
    disableNoiseSuppression: micProcessingPreferences.disableNoiseSuppression === true,
    disableAutoGainControl: micProcessingPreferences.disableAutoGainControl === true
  };
}

function hasUserDisabledMicProcessing(micProcessingPreferences) {
  return (
    micProcessingPreferences.disableEchoCancellation ||
    micProcessingPreferences.disableNoiseSuppression ||
    micProcessingPreferences.disableAutoGainControl
  );
}

function shouldUseRnnoise(micProcessingPreferences) {
  return !micProcessingPreferences.disableNoiseSuppression;
}

function echoCancellationConstraint(micProcessingPreferences) {
  return micProcessingPreferences.disableEchoCancellation ? false : { ideal: "all" };
}

function rnnoiseProcessingLabel(micProcessingPreferences) {
  return micProcessingPreferences.disableEchoCancellation
    ? "no-aec+rnnoise+light-gate"
    : MIC_AUDIO_PROCESSING_LABELS.RNNOISE_WITH_GATE;
}

function withCommonMicConstraints(audioConstraints, overrides, micProcessingPreferences) {
  const baseConstraints = { ...(audioConstraints || {}) };
  delete baseConstraints.echoCancellation;
  delete baseConstraints.noiseSuppression;
  delete baseConstraints.autoGainControl;
  delete baseConstraints.channelCount;
  delete baseConstraints.sampleRate;

  return {
    ...baseConstraints,
    echoCancellation: echoCancellationConstraint(micProcessingPreferences),
    ...overrides,
    channelCount: { ideal: 1 },
    sampleRate: { ideal: MIC_AUDIO_SAMPLE_RATE }
  };
}

export function buildRnnoiseMicConstraints(audioConstraints = {}, micProcessingPreferences = {}) {
  const normalizedPreferences = normalizeMicProcessingPreferences(micProcessingPreferences);

  return withCommonMicConstraints(
    audioConstraints,
    {
      noiseSuppression: false,
      autoGainControl: false
    },
    normalizedPreferences
  );
}

export function buildNativeProcessedMicConstraints(audioConstraints = {}, micProcessingPreferences = {}) {
  const normalizedPreferences = normalizeMicProcessingPreferences(micProcessingPreferences);

  return withCommonMicConstraints(
    audioConstraints,
    {
      noiseSuppression: !normalizedPreferences.disableNoiseSuppression,
      autoGainControl: !normalizedPreferences.disableAutoGainControl
    },
    normalizedPreferences
  );
}

function getDefaultMediaDevices() {
  return typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
}

function getDefaultAudioContextConstructor() {
  if (typeof window === "undefined") return undefined;
  return window.AudioContext || window.webkitAudioContext;
}

function stopStreamTracks(stream) {
  stream?.getTracks?.().forEach(track => track.stop());
}

function disconnectNode(node) {
  try {
    node?.disconnect?.();
  } catch {
    // A partially initialized graph can leave nodes already disconnected.
  }
}

function destroyWorkletNode(node) {
  try {
    node?.destroy?.();
  } catch {
    // Worklet cleanup is best-effort; track and context shutdown still release capture.
  }
}

async function closeAudioContext(audioContext) {
  if (!audioContext || audioContext.state === "closed") return;
  await audioContext.close?.();
}

function configureMicLimiter(limiter) {
  limiter.threshold.value = MIC_LIMITER_OPTIONS.threshold;
  limiter.knee.value = MIC_LIMITER_OPTIONS.knee;
  limiter.ratio.value = MIC_LIMITER_OPTIONS.ratio;
  limiter.attack.value = MIC_LIMITER_OPTIONS.attack;
  limiter.release.value = MIC_LIMITER_OPTIONS.release;
}

function createProcessingCleanup({ source, rnnoise, gate, makeupGain, limiter, rawStream, cleanStream, audioContext }) {
  let cleanedUp = false;

  return async () => {
    if (cleanedUp) return;
    cleanedUp = true;

    disconnectNode(source);
    disconnectNode(rnnoise);
    disconnectNode(gate);
    disconnectNode(makeupGain);
    disconnectNode(limiter);
    destroyWorkletNode(rnnoise);
    stopStreamTracks(rawStream);
    stopStreamTracks(cleanStream);
    await closeAudioContext(audioContext);
  };
}

export async function getNativeProcessedMicStream({
  audioConstraints = {},
  micProcessingPreferences = {},
  mediaDevices = getDefaultMediaDevices()
} = {}) {
  const stream = await mediaDevices.getUserMedia({
    audio: buildNativeProcessedMicConstraints(audioConstraints, micProcessingPreferences),
    video: false
  });

  return stream;
}

export async function getMicStreamForRnnoise({
  audioConstraints = {},
  micProcessingPreferences = {},
  mediaDevices = getDefaultMediaDevices()
} = {}) {
  const stream = await mediaDevices.getUserMedia({
    audio: buildRnnoiseMicConstraints(audioConstraints, micProcessingPreferences),
    video: false
  });

  return stream;
}

async function createNativeMicProcessing(options) {
  const nativeStream = await getNativeProcessedMicStream(options);
  const normalizedPreferences = normalizeMicProcessingPreferences(options?.micProcessingPreferences);

  return {
    rawStream: nativeStream,
    cleanStream: nativeStream,
    processing: hasUserDisabledMicProcessing(normalizedPreferences)
      ? MIC_AUDIO_PROCESSING_LABELS.NATIVE_WITH_USER_SETTINGS
      : MIC_AUDIO_PROCESSING_LABELS.NATIVE,
    audioContext: null,
    cleanup: async () => {
      stopStreamTracks(nativeStream);
    }
  };
}

async function defaultLoadNoiseSuppressor() {
  return import("@sapphi-red/web-noise-suppressor");
}

function getCachedPromise(cache, key, createPromise) {
  let promise = cache.get(key);
  if (!promise) {
    promise = Promise.resolve()
      .then(createPromise)
      .catch(error => {
        if (cache.get(key) === promise) {
          cache.delete(key);
        }
        throw error;
      });
    cache.set(key, promise);
  }

  return promise;
}

function loadNoiseSuppressorModule(loadNoiseSuppressor = defaultLoadNoiseSuppressor) {
  return getCachedPromise(noiseSuppressorModulePromiseCache, loadNoiseSuppressor, loadNoiseSuppressor);
}

function assetDefault(asset) {
  return asset?.default || asset;
}

export function getDefaultNoiseSuppressorAssetUrls() {
  return {
    rnnoiseWorkletUrl: assetDefault(
      require("!!file-loader?outputPath=assets/raw-js&name=[name]-[contenthash].[ext]!@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js")
    ),
    noiseGateWorkletUrl: assetDefault(
      require("!!file-loader?outputPath=assets/raw-js&name=[name]-[contenthash].[ext]!@sapphi-red/web-noise-suppressor/noiseGateWorklet.js")
    ),
    rnnoiseWasmUrl: assetDefault(
      require("!!file-loader?outputPath=assets/wasm&name=[name]-[contenthash].[ext]!@sapphi-red/web-noise-suppressor/rnnoise.wasm")
    ),
    rnnoiseWasmSimdUrl: assetDefault(
      require("!!file-loader?outputPath=assets/wasm&name=[name]-[contenthash].[ext]!@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm")
    )
  };
}

function resolveNoiseSuppressorAssetUrls(assetUrls) {
  return assetUrls || getDefaultNoiseSuppressorAssetUrls();
}

function rnnoiseWasmBinaryCacheFor(loadNoiseSuppressor) {
  let cache = rnnoiseWasmBinaryPromiseCache.get(loadNoiseSuppressor);
  if (!cache) {
    cache = new Map();
    rnnoiseWasmBinaryPromiseCache.set(loadNoiseSuppressor, cache);
  }

  return cache;
}

function rnnoiseWasmBinaryCacheKey(urls) {
  return `${urls.rnnoiseWasmUrl}|${urls.rnnoiseWasmSimdUrl}`;
}

async function loadRnnoiseWasmBinary({ urls, loadNoiseSuppressor, noiseSuppressorModule }) {
  const cache = rnnoiseWasmBinaryCacheFor(loadNoiseSuppressor);
  const cacheKey = rnnoiseWasmBinaryCacheKey(urls);

  return getCachedPromise(cache, cacheKey, async () => {
    const { loadRnnoise } = noiseSuppressorModule || (await loadNoiseSuppressorModule(loadNoiseSuppressor));
    return loadRnnoise({
      url: urls.rnnoiseWasmUrl,
      simdUrl: urls.rnnoiseWasmSimdUrl
    });
  });
}

export async function preloadMicNoiseSuppressor({
  micProcessingPreferences = {},
  assetUrls,
  loadNoiseSuppressor = defaultLoadNoiseSuppressor,
  logger = console
} = {}) {
  const normalizedPreferences = normalizeMicProcessingPreferences(micProcessingPreferences);

  if (!shouldUseRnnoise(normalizedPreferences)) {
    return false;
  }

  try {
    const urls = resolveNoiseSuppressorAssetUrls(assetUrls);
    await loadRnnoiseWasmBinary({ urls, loadNoiseSuppressor });
    return true;
  } catch (error) {
    logger?.warn?.("Unable to preload microphone noise suppression assets.", error);
    return false;
  }
}

export async function createCleanMicStream({
  audioConstraints = {},
  micProcessingPreferences = {},
  mediaDevices = getDefaultMediaDevices(),
  AudioContextConstructor = getDefaultAudioContextConstructor(),
  assetUrls,
  loadNoiseSuppressor = defaultLoadNoiseSuppressor,
  logger = console
} = {}) {
  let rawStream = null;
  let audioContext = null;
  let source = null;
  let rnnoise = null;
  let gate = null;
  let makeupGain = null;
  let limiter = null;
  const normalizedPreferences = normalizeMicProcessingPreferences(micProcessingPreferences);

  if (!shouldUseRnnoise(normalizedPreferences) || !AudioContextConstructor) {
    return createNativeMicProcessing({
      audioConstraints,
      micProcessingPreferences: normalizedPreferences,
      mediaDevices,
      logger
    });
  }

  rawStream = await getMicStreamForRnnoise({
    audioConstraints,
    micProcessingPreferences: normalizedPreferences,
    mediaDevices,
    logger
  });

  try {
    audioContext = new AudioContextConstructor({ sampleRate: MIC_AUDIO_SAMPLE_RATE });

    if (!audioContext.audioWorklet) {
      throw new Error("AudioWorklet is not available for mic noise suppression.");
    }

    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    const noiseSuppressorModule = await loadNoiseSuppressorModule(loadNoiseSuppressor);
    const { RnnoiseWorkletNode, NoiseGateWorkletNode } = noiseSuppressorModule;
    const urls = resolveNoiseSuppressorAssetUrls(assetUrls);
    const rnnoiseWasmBinary = await loadRnnoiseWasmBinary({
      urls,
      loadNoiseSuppressor,
      noiseSuppressorModule
    });

    await Promise.all([
      audioContext.audioWorklet.addModule(urls.rnnoiseWorkletUrl),
      audioContext.audioWorklet.addModule(urls.noiseGateWorkletUrl)
    ]);

    source = audioContext.createMediaStreamSource(rawStream);
    rnnoise = new RnnoiseWorkletNode(audioContext, {
      wasmBinary: rnnoiseWasmBinary,
      maxChannels: 1
    });
    gate = new NoiseGateWorkletNode(audioContext, LIGHT_GATE_OPTIONS);
    makeupGain = audioContext.createGain();
    makeupGain.gain.value = MIC_POST_RNNOISE_GAIN;
    limiter = audioContext.createDynamicsCompressor();
    configureMicLimiter(limiter);

    const destination = audioContext.createMediaStreamDestination();

    source.connect(rnnoise);
    rnnoise.connect(gate);
    gate.connect(makeupGain);
    makeupGain.connect(limiter);
    limiter.connect(destination);

    return {
      rawStream,
      cleanStream: destination.stream,
      processing: rnnoiseProcessingLabel(normalizedPreferences),
      audioContext,
      cleanup: createProcessingCleanup({
        source,
        rnnoise,
        gate,
        makeupGain,
        limiter,
        rawStream,
        cleanStream: destination.stream,
        audioContext
      })
    };
  } catch (error) {
    logger?.warn?.("Falling back to native microphone processing after RNNoise setup failed.", error);

    await createProcessingCleanup({ source, rnnoise, gate, makeupGain, limiter, rawStream, audioContext })();

    return createNativeMicProcessing({
      audioConstraints,
      micProcessingPreferences: normalizedPreferences,
      mediaDevices,
      logger
    });
  }
}
